<template>
  <form
    class="account-view payment-details-form"
    @submit.prevent="goToOrderReview()"
    autocomplete="on"
  >
    <div class="account-view__content">
      <div class="account-view__section">
        <div class="container mb-4">
          <div class="columns is-gapless">
            <div class="column is-two-thirds">
              <h2 class="title is-3 has-text-grey">
                <template v-if="orderTrackingInfo">
                  {{ $t("checkout.paymentDetails.completeYourOrder") }}
                </template>
                <template v-else>
                  {{ $t("checkout.paymentDetails.customerAndPaymentDetails") }}
                </template>
              </h2>
              <h2 class="title is-5 has-text-grey is-uppercase">
                {{ $t("checkout.paymentDetails.customer") }}
              </h2>
            </div>
            <div class="column is-one-third has-text-right">
              <RouterLink
                to="/login"
                v-if="!isUserLoggedIn"
                class="button is-primary-dark is-outlined px-6 mr-5"
              >
                {{ $t("login.mainActions.login") }}
              </RouterLink>
              <button
                :title="
                  !allRequiredFieldsAreFilled
                    ? 'Nicht alle Pflichtfelder ausgefüllt'
                    : '4'
                "
                :disabled="!allRequiredFieldsAreFilled"
                :class="{ 'is-loading': isCurrentlySaving }"
                type="submit"
                class="button is-primary-dark px-6"
              >
                {{ $t("checkout.nextStep") }}
              </button>
            </div>
          </div>
        </div>
      </div>
      <div
        class="account-view__section has-background-grey-lighter"
        @input="addressChanged = true"
      >
        <div class="container">
          <div class="columns is-multiline">
            <div class="column is-one-third-tablet">
              <div class="field">
                <label class="label is-uppercase mt-3 ml-3"
                  >{{
                    $t("checkout.paymentDetails.inputLabels.firstName")
                  }}
                  *</label
                >
                <div class="control">
                  <input
                    v-model="billingAddress.firstname"
                    :disabled="!isEditable"
                    class="input"
                    type="text"
                    maxlength="20"
                    name="given-name"
                    autocomplete="given-name"
                    :placeholder="
                      $t('checkout.paymentDetails.inputLabels.firstName')
                    "
                  />
                </div>
              </div>
            </div>
            <div class="column is-one-third-tablet">
              <div class="field">
                <label class="label is-uppercase mt-3 ml-3"
                  >{{
                    $t("checkout.paymentDetails.inputLabels.lastName")
                  }}
                  *</label
                >
                <div class="control">
                  <input
                    v-model="billingAddress.lastname"
                    :disabled="!isEditable"
                    class="input"
                    type="text"
                    maxlength="20"
                    name="family-name"
                    autocomplete="family-name"
                    :placeholder="
                      $t('checkout.paymentDetails.inputLabels.lastName')
                    "
                  />
                </div>
              </div>
            </div>
            <div class="column is-one-third-tablet">
              <div class="field">
                <label class="label is-uppercase mt-3 ml-3"
                  >{{
                    $t("checkout.paymentDetails.inputLabels.email")
                  }}
                  *</label
                >
                <div class="control">
                  <input
                    v-model="paymentDetails.email"
                    :disabled="!isEditable"
                    class="input"
                    type="email"
                    name="email"
                    autocomplete="email"
                    :placeholder="
                      $t('checkout.paymentDetails.inputLabels.email')
                    "
                  />
                </div>
              </div>
            </div>
            <div class="column is-one-third-tablet">
              <div class="field">
                <label class="label is-uppercase mt-3 ml-3">{{
                  $t("checkout.paymentDetails.inputLabels.phone")
                }}</label>
                <div class="control">
                  <input
                    class="input"
                    :disabled="!isEditable"
                    type="tel"
                    autocomplete="tel"
                    :placeholder="
                      $t('checkout.paymentDetails.inputLabels.phone')
                    "
                    maxlength="15"
                    name="tel"
                    v-model="paymentDetails.phone"
                  />
                </div>
              </div>
            </div>
            <div class="column is-one-third-tablet">
              <b-field
                :label="
                  `${$t('checkout.paymentDetails.inputLabels.referenceNumber')}`
                "
              >
                <b-input
                  :placeholder="
                    $t('checkout.paymentDetails.inputLabels.referenceNumber')
                  "
                  :disabled="!isEditable"
                  v-model="paymentDetails.customerRemarks.referenceNumber"
                ></b-input>
              </b-field>
            </div>
          </div>
        </div>
      </div>

      <div class="account-view__section" @input="addressChanged = true">
        <div class="container mb-5">
          <h2 class="title is-5 has-text-grey is-uppercase">
            {{ $t("checkout.paymentDetails.billingAddress") }}
          </h2>
        </div>
        <div class="container">
          <div class="columns is-multiline">
            <div class="column is-one-third-tablet">
              <div class="field">
                <label class="label is-uppercase mt-3 ml-3">Land *</label>
                <div class="control">
                  <select
                    class="input"
                    v-model="billingAddress.countryId"
                    :disabled="!isEditable"
                    autocomplete="off"
                  >
                    <option
                      v-for="value in availableShippingCountries"
                      :key="`billing-country-option__${value._id}`"
                      :value="value._id"
                    >
                      {{ value.country }}
                    </option>
                  </select>
                </div>
              </div>
            </div>
            <div class="column is-one-third-tablet">
              <b-field
                :label="
                  `${$t('checkout.paymentDetails.inputLabels.postalCode')} *`
                "
              >
                <b-input
                  v-model="billingAddress.postalCode"
                  @input="fetchCitySuggestions($event, 'billingAddress')"
                  :disabled="!isEditable"
                  type="text"
                  name="postal-code"
                  autocomplete="postal-code"
                  v-if="shippingCountryById(billingAddress.countryId)"
                  :placeholder="
                    $t('checkout.paymentDetails.inputLabels.postalCode')
                  "
                  :title="
                    'Gültige Postleitzahl aus ' +
                      shippingCountryById(billingAddress.countryId).country
                  "
                  :pattern="
                    shippingCountryById(billingAddress.countryId)
                      .postalCodeRegex
                  "
                />
              </b-field>
            </div>

            <div class="column is-one-third-tablet">
              <b-field
                :label="`${$t('checkout.paymentDetails.inputLabels.city')} *`"
              >
                <b-input
                  v-model="billingAddress.city"
                  :disabled="!isEditable"
                  :loading="baCitySuggestionLoading"
                  type="text"
                  name="address-level2"
                  autocomplete="address-level2"
                  :placeholder="$t('checkout.paymentDetails.inputLabels.city')"
                />
              </b-field>
            </div>
            <div class="column is-one-third-tablet">
              <div class="field">
                <label class="label is-uppercase mt-3 ml-3"
                  >{{
                    $t("checkout.paymentDetails.inputLabels.street")
                  }}
                  *</label
                >
                <div class="control">
                  <input
                    v-model="billingAddress.street"
                    :disabled="!isEditable"
                    class="input"
                    type="text"
                    name="street-address"
                    autocomplete="street-address"
                    :placeholder="
                      $t('checkout.paymentDetails.inputLabels.street')
                    "
                  />
                </div>
              </div>
            </div>
            <div
              class="column is-one-third-tablet"
              v-if="shippingCountryById(billingAddress.countryId)"
            >
              <div class="field">
                <label class="label is-uppercase mt-3 ml-3"
                  >{{ $t("checkout.paymentDetails.inputLabels.company") }}
                  <template v-if="requiredBillingFields.includes('company')"
                    >*</template
                  >
                </label>
                <div class="control">
                  <input
                    v-model="billingAddress.company"
                    :disabled="!isEditable"
                    class="input"
                    type="text"
                    name="organization"
                    autocomplete="organization"
                    :required="requiredBillingFields.includes('company')"
                    :placeholder="
                      $t('checkout.paymentDetails.inputLabels.company')
                    "
                  />
                </div>
              </div>
            </div>

            <div class="column is-one-third-tablet">
              <fieldset :disabled="billingAddress.company.length === 0">
                <div
                  class="field"
                  :class="{
                    'is-disabled': billingAddress.company.length === 0,
                  }"
                >
                  <label class="label is-uppercase mt-3 ml-3"
                    >{{
                      $t(
                        "checkout.paymentDetails.inputLabels.vatRegistrationNumber"
                      )
                    }}
                    <template v-if="requiresVATRegistrationNumber">*</template>
                  </label>
                  <div class="control">
                    <input
                      :required="requiresVATRegistrationNumber"
                      :placeholder="
                        $t(
                          'checkout.paymentDetails.inputLabels.vatRegistrationNumber'
                        )
                      "
                      :disabled="
                        !isEditable || billingAddress.company.length === 0
                      "
                      class="input"
                      v-model="billingAddress.vatRegistrationNumber"
                      type="text"
                      autocomplete="off"
                    />
                  </div>
                </div>
              </fieldset>
            </div>
          </div>
        </div>
      </div>
      <div class="account-view__section">
        <div class="container mt-5">
          <h2 class="title is-5 has-text-grey is-uppercase">
            {{ $t("checkout.paymentDetails.shippingAddress") }}
          </h2>
        </div>
      </div>
      <div
        class="account-view__section has-background-grey-lighter"
        @input="addressChanged = true"
      >
        <div class="container">
          <div class="field has-checkbox">
            <label class="checkbox">
              <input
                class="control"
                type="checkbox"
                autocomplete="off"
                v-model="shippingAddressDiffersFromBillingAddress"
              />
              {{
                $t(
                  "checkout.paymentDetails.inputLabels.shippingAddressDiffersFromBillingAddress"
                )
              }}
            </label>
          </div>
        </div>
        <transition name="fade">
          <div class="container" v-if="!isBillingAndShippingEqual">
            <div class="columns is-multiline">
              <div class="column is-one-third-tablet">
                <div class="field">
                  <label class="label is-uppercase mt-3 ml-3"
                    >{{
                      $t("checkout.paymentDetails.inputLabels.firstName")
                    }}
                    *</label
                  >
                  <div class="control">
                    <input
                      v-model="shippingAddress.firstname"
                      :disabled="!isEditable"
                      class="input"
                      type="text"
                      name="given-name"
                      maxlength="20"
                      :placeholder="
                        $t('checkout.paymentDetails.inputLabels.firstName')
                      "
                    />
                  </div>
                </div>
              </div>
              <div class="column is-one-third-tablet">
                <div class="field">
                  <label class="label is-uppercase mt-3 ml-3"
                    >{{
                      $t("checkout.paymentDetails.inputLabels.lastName")
                    }}
                    *</label
                  >
                  <div class="control">
                    <input
                      v-model="shippingAddress.lastname"
                      :disabled="!isEditable"
                      class="input"
                      type="text"
                      name="family-name"
                      maxlength="20"
                      :placeholder="
                        $t('checkout.paymentDetails.inputLabels.lastName')
                      "
                    />
                  </div>
                </div>
              </div>

              <div class="column is-one-third-tablet">
                <div class="field">
                  <label class="label is-uppercase mt-3 ml-3">{{
                    $t("checkout.paymentDetails.inputLabels.company")
                  }}</label>
                  <div class="control">
                    <input
                      v-model="shippingAddress.company"
                      :disabled="!isEditable"
                      class="input"
                      type="text"
                      name="organization"
                      :placeholder="
                        $t('checkout.paymentDetails.inputLabels.company')
                      "
                    />
                  </div>
                </div>
              </div>

              <div class="column is-one-third-tablet">
                <div class="field">
                  <label class="label is-uppercase mt-3 ml-3">Land *</label>
                  <div class="control">
                    <select
                      class="input"
                      v-model="shippingAddress.countryId"
                      :disabled="!isEditable"
                      autocomplete="off"
                    >
                      <option
                        v-for="value in availableShippingCountries"
                        :value="value._id"
                        :key="`shipping-country-option__${value._id}`"
                      >
                        {{ value.country }}
                      </option>
                    </select>
                  </div>
                </div>
              </div>

              <div class="column is-one-third-tablet">
                <b-field
                  :label="
                    `${$t('checkout.paymentDetails.inputLabels.postalCode')} *`
                  "
                >
                  <b-input
                    v-model="shippingAddress.postalCode"
                    @input="fetchCitySuggestions($event, 'shippingAddress')"
                    :disabled="!isEditable"
                    type="text"
                    name="postal-code"
                    v-if="shippingCountryById(shippingAddress.countryId)"
                    :placeholder="
                      $t('checkout.paymentDetails.inputLabels.postalCode')
                    "
                    :title="
                      'Gültige Postleitzahl aus ' +
                        'paymentDetails.shippingCountry.name'
                    "
                  />
                </b-field>
              </div>

              <div class="column is-one-third-tablet">
                <b-field
                  :label="`${$t('checkout.paymentDetails.inputLabels.city')}*`"
                >
                  <b-input
                    v-model="shippingAddress.city"
                    :disabled="!isEditable"
                    :loading="saCitySuggestionLoading"
                    type="text"
                    name="address-level2"
                    :placeholder="
                      $t('checkout.paymentDetails.inputLabels.city')
                    "
                  />
                </b-field>
              </div>

              <div class="column is-one-third-tablet">
                <div class="field">
                  <label class="label is-uppercase mt-3 ml-3"
                    >{{
                      $t("checkout.paymentDetails.inputLabels.street")
                    }}
                    *</label
                  >
                  <div class="control">
                    <input
                      v-model="shippingAddress.street"
                      :disabled="!isEditable"
                      class="input"
                      type="text"
                      name="street-address"
                      :placeholder="
                        $t('checkout.paymentDetails.inputLabels.street')
                      "
                    />
                  </div>
                </div>
              </div>
            </div>
          </div>
        </transition>
      </div>
      <div v-if="isBusinessCustomer" class="account-view__section">
        <div class="container mt-5">
          <h2 class="title is-5 has-text-grey is-uppercase">
            {{ $t("checkout.paymentDetails.mailing") }}
          </h2>
        </div>
      </div>

      <div
        v-if="isBusinessCustomer"
        @input="addressChanged = true"
        class="account-view__section has-background-grey-lighter"
      >
        <div class="container">
          <div class="field has-checkbox">
            <label class="checkbox">
              <input
                class="control"
                type="checkbox"
                autocomplete="off"
                v-model="useSeperateBillingEmail"
              />
              {{
                $t(
                  "checkout.paymentDetails.inputLabels.useSeperateBillingEmail"
                )
              }}
            </label>
          </div>
        </div>
        <transition name="fade">
          <div class="container" v-if="useSeperateBillingEmail">
            <div class="columns is-multiline">
              <div class="column is-one-third">
                <b-field
                  :label="
                    `${$t('checkout.paymentDetails.inputLabels.primaryEmail')}`
                  "
                >
                  <b-input
                    v-model="paymentDetails.email"
                    :placeholder="
                      $t('checkout.paymentDetails.inputLabels.email')
                    "
                  ></b-input>
                </b-field>
              </div>
              <div class="column is-one-third">
                <b-field
                  :label="
                    `${$t('checkout.paymentDetails.inputLabels.billingEmail')}`
                  "
                >
                  <b-input
                    v-model="billingAddress.email"
                    :placeholder="
                      $t('checkout.paymentDetails.inputLabels.email')
                    "
                  ></b-input>
                </b-field>
              </div>
            </div>
          </div>
        </transition>
      </div>

      <div class="account-view__section">
        <div class="container mb-5">
          <div class="field field--payment-method">
            <label class="label"
              >{{ $t("checkout.paymentDetails.inputLabels.paymentMethod") }}
            </label>
            <div class="control">
              <label
                :class="{
                  'is-disabled':
                    (option.id === '1' && !invoiceAllowed) ||
                    (option.id === '2' && !prepaidAllowed) ||
                    (option.id === '3' && !paypalAllowed),
                }"
                class="radio"
                v-for="option in availablePaymentMethods"
                :key="`payment-method-option__${option.id}`"
              >
                <input
                  type="radio"
                  :id="option.id"
                  :value="option.key"
                  v-model="paymentDetails.billingPaymentMethod"
                  :disabled="
                    (option.id === '1' && !invoiceAllowed) ||
                      (option.id === '2' && !prepaidAllowed) ||
                      (option.id === '3' && !paypalAllowed)
                  "
                />
                <SvgIcon
                  class="is-primary-dark"
                  v-if="option.icon"
                  :icon="option.icon"
                  :class="{
                    'is-outlined': ['1'].includes(option.id),
                    'is-size-2': option.id === '1',
                    'is-size-1': option.id !== '1',
                    'ml-1': option.id === '4',
                  }"
                ></SvgIcon>
                <span class="mx-3" v-else></span>
                <span>
                  <span>{{
                    option.id === "2" ? "Vorkasse" : option.name
                  }}</span>
                  <span
                    class="radio__details has-text-grey"
                    v-if="option.id === '1'"
                  >
                    {{
                      !isUserLoggedIn
                        ? "Nur für registrierte Kunden"
                        : isForeignCustomer
                        ? "Nur für Kunden aus Deutschland"
                        : ""
                    }}
                  </span>
                </span>
              </label>
            </div>
          </div>
        </div>
      </div>
      <div class="account-view__section">
        <div
          class="container mb-5 has-text-right-tablet"
          v-if="!allRequiredFieldsAreFilled"
        >
          <span class="has-text-primary-dark"
            >Einige mit
            <strong class="has-text-primary-dark">*</strong> markierte
            Pflichtfelder sind noch nicht ausgefüllt.</span
          >
        </div>
        <div class="container primary-actions">
          <RouterLink
            to="/login"
            v-if="!isUserLoggedIn"
            class="button is-primary-dark is-outlined px-6 mr-5"
          >
            {{ $t("login.mainActions.login") }}
          </RouterLink>
          <button
            v-if="!isUserLoggedIn"
            @click.prevent="goToRegister()"
            class="button is-primary-dark px-6 mr-5"
          >
            {{ $t("login.mainActions.register") }}
          </button>
          <span
            class="field has-checkbox my-0"
            v-if="isUserLoggedIn && addressChanged"
          >
            <label class="checkbox">
              <input
                class="control"
                type="checkbox"
                autocomplete="off"
                v-model="saveNewAddress"
              />
              {{ $t("checkout.paymentDetails.inputLabels.saveNewAddress") }}
            </label>
          </span>
          <button
            :title="
              !allRequiredFieldsAreFilled
                ? 'Nicht alle Pflichtfelder ausgefüllt'
                : '4'
            "
            :disabled="!allRequiredFieldsAreFilled"
            :class="{ 'is-loading': isCurrentlySaving }"
            type="submit"
            class="button is-primary-dark px-6"
          >
            {{ $t("checkout.nextStep") }}
          </button>
        </div>
      </div>
    </div>
  </form>
</template>
<script>
import { mapGetters } from "vuex";
import SvgIcon from "../svg/SvgIcon";
import { $axios } from "@/store/modules/axios-instance";

const postalCodeApiUrl = `${process.env.VUE_APP_FULFILLMENT_API_BASE_URL}/shipping/autofill`;

export default {
  name: "PaymentDetailsForm",
  components: { SvgIcon },
  data() {
    return {
      paymentDetails: {
        email: "",
        phone: "",
        billingPaymentMethod: "paypal",
        customerRemarks: {
          referenceNumber: "",
        },
      },
      billingAddress: {
        street: "",
        city: "",
        country: "",
        countryId: "",
        postalCode: "",
        company: "",
        addressSuffix: "",
        title: "",
        firstname: "",
        lastname: "",
        email: "",
        vatRegistrationNumber: "",
      },
      shippingAddress: {
        street: "",
        city: "",
        postalCode: "",
        company: "",
        addressSuffix: "",
        firstname: "",
        lastname: "",
        country: "",
        countryId: "",
      },
      shippingAddressDiffersFromBillingAddress: false,
      useSeperateBillingEmail: false,
      isCurrentlySaving: false,
      addressChanged: false,
      saveNewAddress: false,
      saCitySuggestionLoading: false,
      baCitySuggestionLoading: false,
      isEditable: true,
      requiredShippingFields: [
        "firstname",
        "lastname",
        "street",
        "postalCode",
        "city",
        "countryId",
      ],
      citySuggestions: [],
    };
  },
  computed: {
    ...mapGetters([
      "isUserLoggedIn",
      "availableShippingCountries",
      "userAccountDetails",
      "shippingCountryById",
      "userPreferences",
      "isBusinessCustomer",
      "orderTrackingInfo",
      "cartContainsAttachedDrawings",
    ]),
    requiresVATRegistrationNumber() {
      try {
        return (
          this.userPreferences.isBusinessCustomer.value &&
          this.shippingCountryById(this.billingAddress.countryId).isEU &&
          this.shippingCountryById(this.billingAddress.countryId)
            .countryCode !== "DE"
        );
      } catch (e) {
        console.log(e);
        return false;
      }
    },
    requiredBillingFields() {
      let requiredFields = [
        "firstname",
        "lastname",
        "street",
        "postalCode",
        "city",
        "countryId",
      ];

      if (this.requiresVATRegistrationNumber) {
        requiredFields.push("vatRegistrationNumber");
      }

      if (this.isBusinessCustomer) {
        requiredFields.push("company");
      }

      return requiredFields;
    },
    allRequiredFieldsAreFilled() {
      let result = true;
      this.requiredBillingFields.forEach((value) => {
        if (!this.billingAddress[value]) {
          result = false;
        }
      });
      if (!this.isBillingAndShippingEqual) {
        this.requiredShippingFields.forEach((value) => {
          if (!this.shippingAddress[value]) {
            result = false;
          }
        });
      }
      return result;
    },
    isBillingAndShippingEqual() {
      return !this.shippingAddressDiffersFromBillingAddress;
    },
    availablePaymentMethods() {
      return [
        /**
        {
          id: "2",
          key: "prepaid",
          name: "Vorkasse",
          icon: "icon--web-application_bank-transfer",
        },
        {
          id: "3",
          key: "paypal",
          name: "PayPal",
          icon: "icon--brand_paypal",
        },
        {
          id: "4",
          key: "quote",
          name: "unverbindliches Angebot anfordern",
          icon: "icon--web-application_quote",
        },
         */
        {
          id: "3",
          key: "paypal",
          name: "PayPal",
          icon: "icon--brand_paypal",
        },
      ];
    },
    prepaidAllowed() {
      return !this.cartContainsAttachedDrawings;
    },
    paypalAllowed() {
      return !this.cartContainsAttachedDrawings;
    },
    invoiceAllowed() {
      if (this.cartContainsAttachedDrawings) return false;
      if (this.billingAddress.countryId.length > 0) {
        return (
          this.isUserLoggedIn &&
          this.shippingCountryById(this.billingAddress.countryId).country ===
            "Deutschland"
        );
      }
    },
    quoteAllowed() {
      return this.isUserLoggedIn;
    },
    isForeignCustomer() {
      if (this.billingAddress.countryId.length > 0) {
        if (this.isBillingAndShippingEqual) {
          return (
            this.shippingCountryById(this.billingAddress.countryId).country !==
            "Deutschland"
          );
        }
        return (
          this.shippingCountryById(this.billingAddress.countryId).country !==
            "Deutschland" ||
          this.shippingCountryById(this.shippingAddress.countryId).country !==
            "Deutschland"
        );
      }
      return false;
    },
  },
  methods: {
    async loadOrder() {
      try {
        await this.$store.dispatch("fetchOrderTrackingInfo", {
          orderId: this.$route.params.orderId,
        });
      } catch (e) {
        sendGenericErrorMessage();
        return;
      }

      let billingPaymentMethod;
      switch (this.orderTrackingInfo.billingInformation.paymentMethod) {
        case "quote":
          billingPaymentMethod = "prepaid";
          break;
        case "paypal":
          billingPaymentMethod = "paypal";
          break;
        default:
          billingPaymentMethod = this.orderTrackingInfo.billingInformation
            .paymentMethod;
      }

      this.paymentDetails = {
        ...this.orderTrackingInfo,
        billingPaymentMethod,
      };
      this.billingAddress = { ...this.orderTrackingInfo.billingInformation };
      this.shippingAddress = {
        ...this.orderTrackingInfo.shippingInformation,
      };
      this.isEditable = false;
      this.shippingAddressDiffersFromBillingAddress = true;
    },
    fetchCMSData() {
      this.$store.dispatch("fetchCMSPage", { urlSlug: "/" });
    },
    async fetchShippingAndPaymentMethods() {
      await this.$store.dispatch("fetchShippingCountries");
    },
    async fetchCitySuggestions(postalCode, fetchFor) {
      switch (fetchFor) {
        case "shippingAddress":
          this.saCitySuggestionLoading = true;
          break;
        case "billingAddress":
          this.baCitySuggestionLoading = true;
          break;
      }

      let country = this.shippingCountryById(this[fetchFor].countryId);

      try {
        const response = await $axios.get(
          `${postalCodeApiUrl}/${country.countryCode.toLowerCase()}/${postalCode}`
        );
        setTimeout(() => {
          if (response) {
            this[fetchFor].city = response.data.places[0]["place name"];
          }

          this.saCitySuggestionLoading = false;
          this.baCitySuggestionLoading = false;
        }, 500);
      } catch (e) {
        this.saCitySuggestionLoading = false;
        this.baCitySuggestionLoading = false;
      }
    },
    fetchUserAccountDetails() {
      let vm = this;
      this.$store
        .dispatch("fetchUserAccountDetails")
        .then(() => {
          let billCountry = null;
          let shipCountry = null;

          vm.billingAddress = {
            ...this.billingAddress,
            ...this.userAccountDetails["billingInformation"],
          };

          vm.shippingAddress = {
            ...this.shippingAddress,
            ...this.userAccountDetails["shippingInformation"],
          };

          vm.paymentDetails = {
            email:
              this.paymentDetails.email || this.userAccountDetails.email || "",
            phone:
              this.paymentDetails.phone || this.userAccountDetails.phone || "",
            customerRemarks: this.paymentDetails.customerRemarks || {
              referenceNumber: "",
            },
            billingPaymentMethod: "prepaid",
          };
        })
        .catch(() => {});
    },
    getQuote() {
      if (!this.isUserLoggedIn) {
        this.$router.push("/login");
      } else {
        this.paymentDetails.payment_method = "quote";
        this.goToOrderReview();
      }
    },
    async storePaymentDetails() {
      let paymentDetails = {
        ...this.paymentDetails,
        billingAddress: this.billingAddress,
        shippingAddress: this.shippingAddress,
        isBillingAndShippingEqual: this.isBillingAndShippingEqual,
      };
      if (this.isUserLoggedIn) {
        paymentDetails.saveNewAddress = this.saveNewAddress;
      }
      if (this.isBillingAndShippingEqual) {
        paymentDetails.shippingAddress = this.billingAddress;
      }
      await this.$store.dispatch("updatePaymentDetails", {
        paymentDetails,
      });
    },
    async goToRegister() {
      await this.storePaymentDetails();
      await this.$router.push({ path: "/register" });
    },
    async goToOrderReview() {
      await this.storePaymentDetails();
      await this.$router.push({
        path: `/review-order/${
          this.$route.params.orderId ? this.$route.params.orderId : ""
        }`,
      });
    },
  },
  watch: {
    isForeignCustomer(value) {
      if (value === true) {
        this.paymentDetails.payment_method = "bankTrans";
      }
    },
    invoiceAllowed(value) {
      if (value === false) {
        this.paymentDetails.payment_method = "bankTrans";
      }
    },
  },
  async created() {
    if (this.$route.params.orderId) {
      await this.loadOrder();
    } else {
      let storedPaymentDetails = this.$store.getters.paymentDetails;
      if (Object.keys(storedPaymentDetails).length > 0) {
        this.paymentDetails = { ...storedPaymentDetails };
        this.billingAddress = { ...storedPaymentDetails.billingAddress };
        this.shippingAddress = { ...storedPaymentDetails.shippingAddress };
        this.shippingAddressDiffersFromBillingAddress = !storedPaymentDetails.isBillingAndShippingEqual;
      }
      if (this.isUserLoggedIn) {
        if (storedPaymentDetails.saveNewAddress) {
          this.addressChanged = true;
        } else {
          await this.fetchUserAccountDetails();
        }
        this.saveNewAddress = storedPaymentDetails.saveNewAddress;
      }
    }

    this.fetchCMSData();
  },
  async mounted() {
    await this.fetchShippingAndPaymentMethods();
    if (!this.billingAddress.countryId) {
      this.billingAddress.countryId = this.availableShippingCountries[0]._id;
      this.shippingAddress.countryId = this.billingAddress.countryId;
    }
  },
  async beforeDestroy() {
    await this.storePaymentDetails();
  },
};
</script>

<style lang="scss">
.payment-details-form {
  padding-top: $header-height-mobile;
  @media screen and (min-width: $desktop) {
    padding-top: 0;
  }
}
</style>
